<template>
  <CustomModal
    :id="id"
    modalClass="modal-business-features"
  >
    <template #header>
      <div class="modal-business-features__header">
        <BaseHeading
          level="3"
          color="monochrome-07"
        >
          Upgrade to Business
        </BaseHeading>
        <button
          type="button"
          @click="$modal.hide(id)"
        >
          <icon
            data="@icon/close.svg"
            :fill="false"
            width="var(--icon-24)"
            height="var(--icon-24)"
            color="#81889B"
            class="modal-business-features__close-btn"
          />
        </button>
      </div>
    </template>

    <template #body>
      <div class="modal-business-features__content">
        <div class="modal-business-features__list">
          <div class="modal-business-features__list-label">Business</div>
          <ul class="modal-business-features__list-items">
            <li
              v-for="item in values"
              :key="item.id"
              class="modal-business-features__list-item"
              :class="{ active: selectedFeatureId === item.id }"
              @click="toggleItem(item.id)"
            >
              <div
                :class="[
                  'modal-business-features__item-header',
                  { 'active-title': selectedFeatureId === item.id }
                ]"
              >
                <BaseSubHeading
                  class="modal-business-features__list-item-title"
                  size="large"
                  weight="400"
                  color="monochrome-06"
                  darkColor="monochrome-06"
                >
                  <icon
                    data="@icon/chevron-with-paddings.svg"
                    width="var(--icon-24)"
                    height="var(--icon-24)"
                    class="subscription__feature-icon"
                  />
                  {{ item.label }}
                </BaseSubHeading>
              </div>
              <div
                class="modal-business-features__subtitle"
                :class="{ 'is-open': selectedFeatureId === item.id }"
              >
                <BaseSubHeading
                  class="modal-business-features__list-item-subtitle"
                  size="medium"
                  weight="400"
                  color="monochrome-06"
                  darkColor="monochrome-06"
                >
                  {{ item.subTitle }}
                </BaseSubHeading>
              </div>
            </li>
          </ul>
        </div>
        <div class="modal-business-features__media">
          <div class="modal-business-features__media-visual">
            <img
              :src="require(`@/assets/images/billing-images/${getActiveImage}`)"
              class="modal-business-features__media-image"
              alt=""
            />
          </div>
          <div class="modal-business-features__controls">
            <ControlButtons
              :hideCancel="true"
              @cancel="$modal.hide(id)"
              @save="updateSubscription"
            >
              <template #save> Upgrade to Business </template>
            </ControlButtons>
          </div>
        </div>
      </div>
    </template>
  </CustomModal>
</template>

<script lang="ts" setup>
import { useRouter } from "vue-router"
import { useAcl } from "vue-simple-acl"
import { computed } from "vue"
import { useStore } from "vuex"

import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"
import ControlButtons from "@/components/ControlButtons/index.vue"
import CustomModal from "@/components/Modal/index.vue"

import { TypeOfSubscriptionFeatures } from "@/models"
import { useModal } from "@/plugins/vue-js-modal/index"
import { Actions, Getters } from "@/store"

interface BusinessFeature {
  id: TypeOfSubscriptionFeatures
  label: string
  subTitle: string
  img: string
}

const props = defineProps({
  id: {
    type: String,
    default: "modal-business-features"
  }
})

const activeItemId = computed(() => store.getters[Getters.SELECTED_SUBSCRIPTION_FEATURE])
const selectedFeatureId = computed<TypeOfSubscriptionFeatures>(
  () => activeItemId.value ?? "teamInsights"
)

const toggleItem = (itemId: string) => {
  store.dispatch(
    Actions.SELECTED_SUBSCRIPTION_FEATURE,
    activeItemId.value === itemId ? null : itemId
  )
}

const store = useStore()
const router = useRouter()
const modal = useModal()
const isDark = computed(() => store.getters[Getters.THEME_IS_DARK])

const values = computed((): BusinessFeature[] => {
  return [
    {
      id: "unlimitedProjects",
      label: "Unlimited Projects & Teams",
      subTitle: "Run all of your Projects and Teams in Project Simple, no limit",
      img: isDark.value ? "unlimited-project-billing-dark.png" : "unlimited-project-billing.png"
    },
    {
      id: "multiProjectTeams",
      label: "Multi-Project Teams",
      subTitle: "Easily manage Teams supporting many Projects at the same time",
      img: isDark.value ? "multi-project-team-billing-dark.png" : "multi-project-team-billing.png"
    },
    {
      id: "productMilestones",
      label: "Product Milestones",
      subTitle: "Implement Product Operating Model using goals and milestones",
      img: isDark.value ? "product-milestone-billing-dark.png" : "product-milestone-billing.png"
    },
    {
      id: "aiForcecasting",
      label: "AI Forecasting",
      subTitle: "Detect early delivery risks of Project Goals and Milestones",
      img: isDark.value ? "ai-forcast-billing-dark.png" : "ai-forcast-billing.png"
    },
    {
      id: "jiraImport",
      label: "Jira Import",
      subTitle: "Pull all work from all Jira workspaces into Centralized Backlog",
      img: isDark.value ? "jira-imports-billing-dark.png" : "jira-imports-billing.png"
    },
    {
      id: "backlogAnalytics",
      label: "Backlog Analytics",
      subTitle: "Get a detailed view to help manage the Product Backlog",
      img: isDark.value ? "backlog-analytics-billing-dark.png" : "backlog-analytics-billing.png"
    },
    {
      id: "sprintAnalytics",
      label: "Sprint Analytics",
      subTitle: "Get visibility how the Teams work and where they can improve",
      img: isDark.value ? "sprint-analytics-billing-dark.png" : "sprint-analytics-billing.png"
    },
    {
      id: "itemAnalytics",
      label: "Item Analytics",
      subTitle: "Transparent view into each Backlog Item performance",
      img: isDark.value ? "item-analytics-billing-dark.png" : "item-analytics-billing.png"
    },
    {
      id: "teamsHealthCheck",
      label: "Team Health Check",
      subTitle: "Anonymous survey to assess Team's Health and Wellness",
      img: isDark.value ? "team-health-check-billing-dark.png" : "team-health-check-billing.png"
    },
    {
      id: "teamInsights",
      label: "Team Insights",
      subTitle:
        "Understand team capacity, contribution, workload distribution, and delivery patterns.",
      img: isDark.value ? "team-insights-billing-dark.png" : "team-insights-billing.png"
    }
  ]
})

const getActiveImage = computed(() => {
  const activeItem = values.value.find(item => item.id === selectedFeatureId.value)
  return activeItem?.img || values.value[0].img
})

function updateSubscription() {
  modal.hide(props.id)
  if (useAcl().can.any(["admin", "superAdmin"])) {
    router.push({
      name: "edit-account",
      query: {
        tab: "subscription"
      }
    })
  } else {
    modal.show("upgrade-plan-without-permission")
  }
}
</script>
